import { EVENT } from "@/constants/event";
import useAppStore from "@/context/store";
import { displayThousandNum } from "@/utils/displayNum";
import imageUrl from "@/utils/imageUrl";
import { take, upperFirst } from "lodash";
import { useRouter } from "next/router";
import React, { useCallback, useMemo } from "react";
import { SOURCE_TYPE_ICONS } from "../../constants/sourceTypeIcon";
import FollowerAvatar from "../HBFollowerNav/FollowerAvatar";
import Image from "../Image";
import styles from "./index.module.less";

function FoldCard({ record, columnSize = 240 }) {
  // const domains = useAppStore(s=>s.domains);
  const { domains } = useAppStore(({ domains }) => ({
    domains,
  }));
  const router = useRouter();
  const previews = useMemo(() => {
    return take(record.preview_pins, 4).map((item) => {
      return {
        src: imageUrl(item.file, {
          width: 105,
        }),
        alt: item.raw_text,
        srcset:
          imageUrl(item.file, {
            width: 210,
          }) + " 2x",
      };
    });
  }, [record]);

  const sourceModel = useCallback(
    (source) => {
      const { source_type, source_detail, preview_pins } = source;

      let avatar;

      // 折叠封面兜底
      if (preview_pins && preview_pins.length > 0) {
        // 图片裁剪判断，图图片 width/height > 240 / 184,则计算 width 最小值使得图片高度为184
        const file = preview_pins[0].file;
        const { width, height } = file;

        avatar = imageUrl(file, {
          width: width / height > 240 / 184 ? (width * 184) / height : 240,
        });
      } else {
        avatar = "https://st0.dancf.com/static/02/202210131146-ccb1.png";
      }

      if (source_type === 1) {
        return {
          source_type: source_type,
          source_id: source_detail.board_id,
          source_name: source_detail.title,
          source_avatar: avatar,
          source_link: `/boards/${source_detail.board_id}`,
        };
      } else if (source_type === 2) {
        return {
          source_type: source_type,
          source_id: source_detail.user_id,
          source_name: source_detail.username,
          source_avatar:
            source_detail.avatar_url ||
            imageUrl(source_detail.avatar, { width: 45 }),
          source_link: `/user/${source_detail.urlname}`,
        };
      } else if (source_type === 3) {
        const domain = domains.find(
          (item) => item.domainId === source_detail.domain_id
        );
        return {
          source_type: source_type,
          source_id: source_detail.domain_id,
          source_name: source_detail.domain_name,
          source_avatar: domain ? domain.domainLogo[0]?.url : "",
          source_link: `/domain/${source_detail.domain_id}`,
          source_avatar_text: upperFirst(
            source_detail.root_domain_name?.split("")?.slice(0, 2)?.join("") ||
              ""
          ),
        };
      }
    },
    [domains]
  );

  const sourceDetail = useMemo(() => {
    return sourceModel(record);
  }, [record, sourceModel]);

  const onShowFoldModal = () => {
    useAppStore.setState({ currentFold: { ...record } });
    if (router.pathname === "/follow") {
      window.dispatchEvent(new Event(EVENT.CARD_OPERATE));
    }
    router.push(
      {
        query: {
          ...router.query,
          foldModal: true,
        },
      },
      undefined,
      {
        scroll: false,
      }
    );
  };

  const previewImgWidth = columnSize - 12 * 2;
  const halfPreviewImgWidth = Math.floor(previewImgWidth / 2 - 3);

  const previewPins = useMemo(() => {
    switch (previews.length) {
      case 1:
        return (
          <div className={styles.previewImg}>
            <Image
              className="transparent-img-bg"
              alt={""}
              {...previews[0]}
              width={previewImgWidth}
              height={previewImgWidth}
            />
          </div>
        );
      case 2:
        return (
          <>
            <div className={styles.previewImg}>
              <Image
                className="transparent-img-bg"
                alt={""}
                {...previews[0]}
                width={previewImgWidth}
                height={halfPreviewImgWidth}
              />
            </div>
            <div className={styles.previewImg}>
              <Image
                className="transparent-img-bg"
                alt={""}
                {...previews[1]}
                width={previewImgWidth}
                height={halfPreviewImgWidth}
              />
            </div>
          </>
        );
      case 3:
        return (
          <>
            <div className={styles.previewImg}>
              <Image
                className="transparent-img-bg"
                alt={""}
                {...previews[0]}
                width={previewImgWidth}
                height={halfPreviewImgWidth}
              />
            </div>
            <div className={styles.previewImg}>
              <Image
                className="transparent-img-bg"
                alt={""}
                {...previews[1]}
                width={halfPreviewImgWidth}
                height={halfPreviewImgWidth}
              />
            </div>
            <div className={styles.previewImg}>
              <Image
                className="transparent-img-bg"
                alt={""}
                {...previews[2]}
                width={halfPreviewImgWidth}
                height={halfPreviewImgWidth}
              />
            </div>
          </>
        );
      default:
        return previews.map((item, index) => (
          <div key={index} className={styles.previewImg}>
            <Image
              className="transparent-img-bg"
              alt={""}
              {...item}
              width={halfPreviewImgWidth}
              height={halfPreviewImgWidth}
            />
            {index === 3 && record.folding_num - previews.length > 0 && (
              <div className={styles.mask}>
                +{displayThousandNum(record.folding_num - previews.length)}
              </div>
            )}
          </div>
        ));
    }
  }, [halfPreviewImgWidth, previewImgWidth, previews, record.folding_num]);

  return (
    <div className={styles.fold_card}>
      <div
        className={styles.inner}
        onClick={onShowFoldModal}
        data-gd-click="button_click"
        data-gd-expose="dialog_expose"
        data-dialog-title="折叠"
        data-button-name="折叠"
      >
        <div className={styles.head}>
          <FollowerAvatar
            className={styles.avatar}
            size={columnSize * 0.1875}
            src={sourceDetail.source_avatar}
            alt={sourceDetail.source_avatar_text || sourceDetail.source_name}
            icon={SOURCE_TYPE_ICONS[sourceDetail.source_type]}
          />
          <div>
            <div className={styles.name}>{sourceDetail.source_name}</div>
            <div className={styles.info}>
              近期更新了
              <span className={styles.number}>
                {displayThousandNum(record.folding_num)}张
              </span>
              新采集
            </div>
          </div>
        </div>
        <div className={styles.previews}>{previewPins}</div>
      </div>
    </div>
  );
}

export default React.memo(FoldCard);
